import React, { FC, useState } from 'react';
import { get } from 'lodash';
import Svg from 'svg-inline-react';
import { Appearance, Button } from '@src/components/Button';
import { Avatar } from '@src/components/Avatar';
import api from '@src/util/api';
import { validateEmail } from '@src/util/validation';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { getNameInitials } from '@src/util/getNameInitials';
import { idify } from '@src/util/idify';
import { setErrorFlashMessage, setSuccessFlashMessage } from '@src/redux/actions/flashMessages';
import CloseSvg from '@src/svgs/fr-close.svg';
import {
  CollaboratorsForOpeningQuery,
  useCollaboratorsForOpeningQuery,
  useRemoveCollaboratorMutation,
} from '@src/graphql/generated';
import { TextField } from '@src/components/TextField';
import { useAppDispatch } from '@src/redux';
import { trackEvent } from '@src/metrics';
import { InviteCollaboratorsProps } from './props';
import './styles.scss';

enum InviteCollaboratorsError {
  ACCOUNT_ALREADY_EXISTS = 'Looks like an account with this email already exists.',
  ALREADY_INVITED = 'Invitation already has been sent.',
  ALREADY_TEAM_MEMBER = 'Looks like this account is already part of your team.',
  CONTACT_SUPPORT = 'Could not invite collaborator. Please contact support for assistance',
  WRONG_EMAIL = 'Please provide valid email address',
}

export const InviteCollaborators: FC<InviteCollaboratorsProps> = ({ opening, onClose }) => {
  const dispatch = useAppDispatch();

  const [inviteEmail, setInviteEmail] = useState('');
  const [isAddingCollaborator, setIsAddingCollaborator] = useState(false);

  const {
    data,
    loading: isCollaboratorsQueryLoading,
    updateQuery: updateCollaboratorsQuery,
    refetch: refetchCollaboratorsQuery,
  } = useCollaboratorsForOpeningQuery({
    variables: {
      openingId: opening.id,
      paginationOptions: {
        limit: 25,
      },
    },
  });

  const [removeCollaborator] = useRemoveCollaboratorMutation();
  const onRemoveCollaborator = async (recruiterId: string) => {
    try {
      trackEvent({
        context: MetricContext.INVITING_COLLABORATORS,
        interaction: MetricAction.SET_COLLABORATORS,
        payload: {
          'Action': 'Remove',
          'Opening Id': opening.id,
        },
      });

      await removeCollaborator({
        variables: {
          openingId: opening.id,
          recruiterId: recruiterId,
        },
      });

      updateCollaboratorsQuery(previousCollaboratorsQuery => {
        return {
          ...previousCollaboratorsQuery,
          opening: {
            ...previousCollaboratorsQuery.opening,
            collaborators: {
              ...previousCollaboratorsQuery.opening.collaborators,
              result: previousCollaboratorsQuery.opening.collaborators.result.filter(({ id }) => id !== recruiterId),
            },
          },
        };
      });

      dispatch(setSuccessFlashMessage('Collaborator got successfully removed'));
    } catch (error) {
      dispatch(setErrorFlashMessage());
    }
  };

  const onAddCollaborator = async () => {
    if (!inviteEmail || !validateEmail(inviteEmail)) {
      dispatch(setErrorFlashMessage(InviteCollaboratorsError.WRONG_EMAIL));
    } else {
      setIsAddingCollaborator(true);

      try {
        trackEvent({
          context: MetricContext.INVITING_COLLABORATORS,
          interaction: MetricAction.CONFIRM_COLLABORATORS,
          payload: { 'Opening Id': idify(opening) },
        });

        await api.post('/api/team/invites/collaborator', {
          email: inviteEmail,
          openingId: opening.id,
          role: 'collaborator',
        });

        refetchCollaboratorsQuery({
          openingId: opening.id,
          paginationOptions: {
            limit: 25,
          },
        });

        dispatch(setSuccessFlashMessage(`Invite has been sent to ${inviteEmail}`));
        setInviteEmail('');
        setIsAddingCollaborator(false);
      } catch (e) {
        setIsAddingCollaborator(false);
        dispatch(setErrorFlashMessage(InviteCollaboratorsError[e.body && e.body.key]));
      }
    }
  };

  const collaborators: CollaboratorsForOpeningQuery['opening']['collaborators']['result'] = get(
    data,
    'opening.collaborators.result',
    [],
  );

  if (isCollaboratorsQueryLoading) {
    return (
      <div className="invite-collaborators">
        <div>
          <h4>Loading...</h4>
        </div>
      </div>
    );
  }

  return (
    <div className="invite-collaborators">
      <div className="invite-collaborators__title">Manage collaborators</div>

      <div className="invite-collaborators__sub-title">Add or remove people who can access this opening.</div>

      <div className="invite-collaborators__input">
        <TextField
          defaultValue={inviteEmail}
          fullWidth
          label="Invite"
          onBlur={() =>
            trackEvent({
              context: MetricContext.INVITING_COLLABORATORS,
              interaction: MetricAction.SET_COLLABORATORS,
              payload: {
                'Action': 'Add',
                'Opening Id': idify(opening),
              },
            })
          }
          onChange={setInviteEmail}
          placeholder="Invite"
        />
      </div>

      {collaborators.length > 0 && (
        <div className="invite-collaborators__container">
          <div className="invite-collaborators__list-title">Collaborators</div>

          <div className="invite-collaborators__list">
            {collaborators.map(recruiter => {
              const { id, fullName, email, avatarColor } = recruiter;
              const name = fullName || email;

              return (
                <div className="invite-collaborators__item" key={id}>
                  <div className="invite-collaborators__avatar">
                    <Avatar avatarColor={avatarColor} initials={getNameInitials(fullName || email)} size="large" />
                  </div>

                  <div className="invite-collaborators__info">
                    <div className="invite-collaborators__info-top">{name}</div>

                    {fullName ? <div className="invite-collaborators__info-bot">{email}</div> : null}
                  </div>

                  <div className="invite-collaborators__status">
                    {collaborators.some(({ id }) => id === recruiter.id) ? 'Active' : 'Invite sent'}
                  </div>

                  <div className="invite-collaborators__delete">
                    <Svg
                      className="invite-collaborators__delete-icon"
                      onClick={() => onRemoveCollaborator(recruiter.id)}
                      src={CloseSvg}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="invite-collaborators__buttons">
        <Button isDisabled={isAddingCollaborator || !inviteEmail} onClick={onAddCollaborator}>
          Confirm
        </Button>

        <Button appearance={Appearance.SECONDARY} className="invite-collaborators__close-button" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
};
